import axios from 'axios';
import type { StepBand } from './categoryBands';
import { bookOf, replaceBook, type Fitting } from './assemblyStore';
import { gestureIds, pieceIdByKey, pieceItemId, type SubStep } from './stepsRun';
import { bumpSubstepOutputs } from './substepOutputs';

// WHEN PHASE 4 CUTS A STEP AGAIN, EVERY ANSWER ABOUT ITS SUB-STEPS GOES WHERE ITS GESTURE WENT.
//
// Phase 6 files its answers by position — `<step>#<letter>` for a fitting, and for the piece a
// signed-off sub-step leaves (see save_substep_output). A re-run that splits one gesture in two
// moves every gesture after it one letter on, and the answers stayed put: "barrel nut into 109",
// signed off at 1-10, went on standing at 1-10 under "the prepared screw into 109", ticked.
//
// So the step's sub-steps before and after are matched by what they DO (see gestureIds), and:
//   - each answer moves to its gesture's new key — fitting and piece both, so a sign-off keeps
//     its piece and every sub-step standing on that piece keeps its model;
//   - an answer whose gesture is no longer in the step is SHELVED under its print, not dropped,
//     and comes back if a later run says that gesture again;
//   - a sub-step no answer matches starts blank, whatever an older run left at its key;
//   - the step's pieces are renumbered with their gestures, in every answer that names one and in
//     the later steps' own sub-steps, which still say "113" for what is now 115.
//
// Returns the other steps' sub-steps with this step's pieces renamed, for the run to persist, and
// whether the server failed to move the pieces — said by the run, since the fittings have moved
// and their pieces have not.
export async function refileStep(
  docId: string,
  steps: StepBand[],
  was: Record<string, SubStep[]>,
  now: Record<string, SubStep[]>,
  category: string,
): Promise<{ others: Record<string, SubStep[]>; piecesFailed: boolean }> {
  const keysIn = (m: Record<string, SubStep[]>) => (m[category] ?? []).map((s) => `${category}#${s.letter}`);
  const wasIds = gestureIds(steps, was);
  const nowIds = gestureIds(steps, now);
  const shelf = (id: string) => `shelf:${category}:${id}`;
  const book = bookOf(docId);

  // FROM → TO, every one at once: a key may be both, when two gestures trade places.
  const moves = new Map<string, string>();
  const oldByIdent = new Map(keysIn(was).map((key) => [wasIds.get(key)!, key]));
  const targeted = new Set<string>();
  for (const key of keysIn(now)) {
    const id = nowIds.get(key)!;
    const from = oldByIdent.get(id) ?? (book.bySlot[shelf(id)] ? shelf(id) : null);
    if (!from) continue;
    oldByIdent.delete(id);
    moves.set(from, key);
    targeted.add(key);
  }
  for (const [id, key] of oldByIdent) moves.set(key, shelf(id));

  // THE STEP'S PIECES, old number to new, as item ids (see pieceItemId). Null where the gesture
  // that made it is gone: an answer naming it names nothing any more.
  const wasPieces = pieceIdByKey(steps, was);
  const nowPieces = pieceIdByKey(steps, now);
  const pieceMap = new Map<string, string | null>();
  for (const key of keysIn(was)) {
    const old = wasPieces.get(key)?.id;
    if (!old) continue;
    const to = moves.get(key);
    pieceMap.set(old, (to && nowPieces.get(to)?.id) || null);
  }
  const itemMap = new Map([...pieceMap].map(([a, b]) => [pieceItemId(a)!, b == null ? null : pieceItemId(b)]));
  const renumber = (id: number | null): number | null => (id != null && itemMap.has(id) ? itemMap.get(id)! : id);
  const renumbered = (f: Fitting): Fitting => ({
    ...f,
    targetId: renumber(f.targetId),
    movingId: renumber(f.movingId),
    excluded: f.excluded.map(renumber).filter((id): id is number => id != null),
  });

  const bySlot: Record<string, Fitting> = {};
  for (const [key, f] of Object.entries(book.bySlot)) {
    if (moves.has(key)) continue;
    // A sub-step of this step nothing was matched to: blank, not whatever an older run left.
    if (keysIn(now).includes(key) && !targeted.has(key)) continue;
    bySlot[key] = renumbered(f);
  }
  for (const [from, to] of moves) {
    const f = book.bySlot[from];
    if (f) bySlot[to] = renumbered(f);
  }
  const at = moves.get(book.at);
  replaceBook(docId, { at: at && !at.startsWith('shelf:') ? at : book.at, bySlot });

  let piecesFailed = false;
  if (moves.size) {
    // AND THE PIECES, filed on the server under the same keys. Their names go with them: a piece
    // is found by its key AND its output's name (see bakedFor), and the model words the name
    // afresh every run — which is why this is sent even where no gesture moved.
    const labels: Record<string, string> = {};
    for (const to of moves.values()) {
      const made = nowPieces.get(to);
      if (made) labels[to] = `${made.id} – ${made.output.en || made.output.fr}`;
    }
    try {
      await axios.post('/api/notice/3d/substep/refile', { docId, moves: Object.fromEntries(moves), labels });
    } catch {
      piecesFailed = true;
    }
    bumpSubstepOutputs(docId);
  }

  // THE LATER STEPS STILL SAY THE OLD NUMBERS — they were asked when the step had its old cut.
  // A piece whose gesture is gone keeps its number: there is nothing true to rename it to.
  const renamed = (id: string) => pieceMap.get(id) ?? id;
  const tokens = (text: string) => text.replace(/\{(\d+)\}/g, (all, id) => (pieceMap.get(id) ? `{${pieceMap.get(id)}}` : all));
  const others = Object.fromEntries(Object.entries(now)
    .filter(([c]) => c !== category)
    .map(([c, subs]) => [c, subs.map((sub) => ({
      ...sub,
      textEn: tokens(sub.textEn),
      textFr: tokens(sub.textFr),
      parts: sub.parts.map((p) => ({ ...p, id: renamed(p.id) })),
      tools: sub.tools.map(renamed),
    }))]));
  return { others, piecesFailed };
}
